import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { MEMBERSHIP_BILLING_ENABLED } from '../lib/membership'
import type { ClubMembership } from '../lib/types'
import { useAuth } from './AuthContext'

/**
 * The signed-in member's standing, fetched ONCE per session and shared.
 *
 * It used to be a bare hook, which was fine while only one screen read it. The
 * clubhouse gate reads it too, so a bare hook would mean /clubhouse/account
 * fetching its standing twice — and, worse, `refresh()` after a checkout would
 * update the screen's copy while the gate went on believing the old answer.
 *
 * The answer FOLLOWS THE SESSION. It used to be fetched once per page load,
 * which meant a magic-link sign-in — a SPA navigation, never a reload — left
 * the gate holding the answer it got while signed out (`null`) and sent a
 * member in good standing to /join/checkout; and signing out left the last
 * member's standing behind for the next visitor on the same screen. Each
 * answer is stamped with the session EPOCH it was fetched in — a counter
 * bumped, during render, every time the session changes, to and from null
 * included — rather than with the session id: a new session asks again, no
 * session holds nothing, a reply that lands for a session that has since
 * ended is ignored, and signing out and back in as the SAME member asks again
 * instead of being served the previous session's answer on its first render.
 * Requests are numbered too, and only the latest one's reply may land, so a
 * slow first fetch can never overwrite a fresher `refresh()`.
 *
 * `null` is a normal state, not an error: most visitors have no standing and
 * the API says so with a null rather than a 404. But `null` is ALSO what a
 * failed fetch leaves behind, and the two must read differently: "holds
 * nothing" sends a member to settle their dues, "could not ask" must not —
 * that member may well have paid. `failed` is how a caller tells an outage
 * from an honest null; it is never raised by the billing-dark skip, where
 * nothing was asked.
 */
interface ClubMembershipValue {
  membership: ClubMembership | null
  /** Signed in, billing on, and no answer for THIS session yet. */
  isLoading: boolean
  /** The last fetch was rejected, so `membership: null` means nothing. */
  failed: boolean
  refresh: () => Promise<void>
}

/** One fetch's outcome, stamped with the session epoch it was asked in. */
interface Answer {
  epoch: number
  membership: ClubMembership | null
  failed: boolean
}

const ClubMembershipContext = createContext<ClubMembershipValue | null>(null)

export function ClubMembershipProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const session = user?.id ?? null

  // The session epoch: bumped every time the session changes, to and from
  // null included. Adjusted DURING render (React's "storing information from
  // previous renders" pattern), never in an effect, so no render — not even
  // the first one after a sign-in — can see an answer from the session
  // before. Stamping by session id would serve a member who signed out and
  // back in the old answer for a beat; the epoch makes it a new question.
  const [tracked, setTracked] = useState({ session, epoch: 0 })
  let epoch = tracked.epoch
  if (tracked.session !== session) {
    epoch += 1
    setTracked({ session, epoch })
  }

  const [answer, setAnswer] = useState<Answer | null>(null)
  // Numbers every request; a reply lands only if nothing newer has been asked.
  const seqRef = useRef(0)

  // The fetch itself, with every setState confined to the promise callbacks so
  // the session effect can call it without setting state synchronously. The
  // endpoint 404s behind the feature flag, and a console full of expected 404s
  // trains people to ignore real ones — hence the guard. Nothing is asked for
  // a visitor either: the API could only say null.
  const load = useCallback((forEpoch: number) => {
    if (!MEMBERSHIP_BILLING_ENABLED) return Promise.resolve()
    const seq = ++seqRef.current
    return api
      .get<{ membership: ClubMembership | null }>('/club/membership')
      .then((data) => {
        if (seq !== seqRef.current) return
        setAnswer({ epoch: forEpoch, membership: data.membership, failed: false })
      })
      .catch(() => {
        if (seq !== seqRef.current) return
        // A member who can't load their standing still gets the page; the
        // standing region says it could not be read and offers to ask again.
        // `failed` is what keeps the gate from reading that as "holds nothing".
        setAnswer({ epoch: forEpoch, membership: null, failed: true })
      })
  }, [])

  // Ask once per session — when it appears or changes — and never for a
  // visitor. Sign-out needs no reset of its own: an answer stamped with an
  // earlier epoch simply stops being current (see `current` below).
  useEffect(() => {
    if (session === null) return
    void load(epoch)
  }, [session, epoch, load])

  // Manual re-syncs (after a checkout) drop the current answer first, so every
  // reader shows loading until the new one lands — the welcome page relies on
  // that to wait for the webhook rather than flash the old standing.
  const refresh = useCallback(async () => {
    if (!MEMBERSHIP_BILLING_ENABLED || session === null) return
    setAnswer(null)
    await load(epoch)
  }, [load, session, epoch])

  const current = answer !== null && answer.epoch === epoch ? answer : null

  const value = useMemo<ClubMembershipValue>(
    () => ({
      membership: current?.membership ?? null,
      failed: current?.failed ?? false,
      isLoading: MEMBERSHIP_BILLING_ENABLED && session !== null && current === null,
      refresh,
    }),
    [current, session, refresh],
  )

  return <ClubMembershipContext.Provider value={value}>{children}</ClubMembershipContext.Provider>
}

/**
 * Outside a provider this returns the honest empty answer rather than throwing.
 * The showcase board and isolated component tests render screens without the
 * app shell, and a screen that explodes there would be untestable for reasons
 * that have nothing to do with what it renders.
 */
export function useClubMembershipContext(): ClubMembershipValue {
  return (
    useContext(ClubMembershipContext) ?? {
      membership: null,
      isLoading: false,
      failed: false,
      refresh: async () => {},
    }
  )
}
